/**
 * 列のドラッグ&ドロップを管理する。
 * ハンドルは持たず、列が選択されているときだけヘッダーセル全体を掴んで
 * 並び替えできる(スプレッドシートと同じUX)。
 */
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { useDrag, useDrop } from 'react-dnd'

const TABLE_COLUMN_DND_TYPE = 'TABLE_COLUMN'

type ColumnDragItem = { columnIndex: number }

type Props = {
  columnIndex: number
  onReorder: (fromIndex: number, toIndex: number) => void
  /** 選択中のときだけ掴んで移動できる。 */
  draggable: boolean
  /** セル全体をクリックで列を選択(アクティブ化)する。 */
  onClick?: () => void
  children: ReactNode
  sx?: SxProps<Theme>
  /** 2段ヘッダーで列を1段の高さに保つための rowSpan。 */
  rowSpan?: number
}

/** 列ヘッダーのセル。選択中はドラッグ元、常にドロップ先になる単一の `<th>`。 */
export const TableColumnHeaderCell = ({
  columnIndex,
  onReorder,
  draggable,
  onClick,
  children,
  sx,
  rowSpan,
}: Props) => {
  const [{ isDragging }, drag] = useDrag<
    ColumnDragItem,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: TABLE_COLUMN_DND_TYPE,
      item: { columnIndex },
      canDrag: draggable,
      collect: monitor => ({ isDragging: monitor.isDragging() }),
    }),
    [columnIndex, draggable],
  )

  const [{ isOver, fromIndex }, drop] = useDrop<
    ColumnDragItem,
    void,
    { isOver: boolean; fromIndex: number }
  >(
    () => ({
      accept: TABLE_COLUMN_DND_TYPE,
      collect: monitor => {
        const dragged = monitor.getItem()
        return {
          isOver: monitor.isOver() && dragged?.columnIndex !== columnIndex,
          fromIndex: dragged?.columnIndex ?? -1,
        }
      },
      drop: item => {
        if (item.columnIndex !== columnIndex) {
          onReorder(item.columnIndex, columnIndex)
        }
      },
    }),
    [columnIndex, onReorder],
  )

  // 押出し(splice)並び替えのため、差し込み位置はドラッグ方向で変わる。
  // 元より右の列に重ねたらその列の右側、左の列なら左側に差し込まれる。
  const insertSide: 'left' | 'right' =
    fromIndex < columnIndex ? 'right' : 'left'

  return (
    <Box
      component="th"
      ref={(node: HTMLTableCellElement | null) => {
        drag(drop(node))
      }}
      rowSpan={rowSpan}
      onClick={onClick}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        {
          p: 0,
          boxSizing: 'border-box',
          transition: 'background-color 120ms ease, box-shadow 120ms ease',
        },
        draggable
          ? { cursor: isDragging ? 'grabbing' : 'grab' }
          : onClick && { cursor: 'pointer' },
        isDragging && { opacity: 0.4 },
        // 差し込まれる位置(列の境界)に青い縦線を出して、どこに入るか分かりやすくする。
        // 本体行まで貫くよう下方向に大きく伸ばし、下端はテーブルラッパの overflow:clip でクリップする。
        isOver && {
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            height: '9999px',
            // 境界の内側に収める。はみ出すとテーブルラッパの overflow:clip で
            // 最終列の右線・先頭列の左線がクリップされて見えなくなるため。
            [insertSide]: 0,
            width: '3px',
            bgcolor: 'primary.main',
            borderRadius: '2px',
            zIndex: 2,
            pointerEvents: 'none',
          },
        },
      ]}
    >
      {children}
    </Box>
  )
}
